import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { PageStatusEnumValidationSchema, PageTypeEnumValidationSchema } from './enum';

// A repeated query key (`type=checkout&type=event`) parses to an array, a single one to a string.
const oneOrMany = <T extends z.ZodType>(item: T) =>
  z.preprocess(value => (typeof value === 'string' ? [value] : value), z.array(item));

export const PageSummariesQueryValidationSchema = z.object({
  type: oneOrMany(PageTypeEnumValidationSchema).optional(),
  status: oneOrMany(PageStatusEnumValidationSchema).optional(),
  search: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(10),
  skip: z.coerce.number().int().min(0).default(0),
});

const BillingConfigSummarySchema = z.looseObject({
  interval: z.string().nullish(),
  intervalCount: z.number().nullish(),
});

const PriceSummarySchema = z.looseObject({
  enabled: z.boolean().nullish(),
  billingType: z.string().nullish(),
  amount: z.number().nullish(),
  currency: z.string().nullish(),
  payWhatYouWant: z.boolean().nullish(),
  recurring: BillingConfigSummarySchema.nullish(),
  paymentPlan: BillingConfigSummarySchema.nullish(),
});

const ProductSummarySchema = z.looseObject({
  _id: ObjectIdStringValidationSchema,
  prices: z.array(PriceSummarySchema).nullish(),
  price: z.number().nullish(),
  currency: z.string().nullish(),
  type: z.string().nullish(),
  interval: z.string().nullish(),
  intervalCount: z.number().nullish(),
  payWhatYouWant: z.boolean().nullish(),
});

export const PageSummaryValidationSchema = z.object({
  _id: ObjectIdStringValidationSchema,
  type: PageTypeEnumValidationSchema,
  name: z.string().nullish(),
  locale: z.string().nullish(),
  eventDetails: z
    .object({
      startDate: z.iso.datetime().nullish(),
      endDate: z.iso.datetime().nullish(),
      timezone: z.string().nullish(),
      currency: z.string().nullish(),
    })
    .nullish(),
  product: ProductSummarySchema.nullish(),
});

export const ListPageSummariesResponseValidationSchema = z.object({
  data: z.array(PageSummaryValidationSchema),
  has_more: z.boolean(),
});
